import { supabase } from "@/integrations/supabase/client";
import type { AuctionCardData } from "@/components/auction-card";

const CARD_COLS =
  "id,title,images,end_time,current_bid_pence,starting_price_pence,bid_count,grading_company,grade,card_type,custom_grading_company,custom_grade,product_type,accessory_category,brand,product_line_set,compatible_game,card_set,card_number,card_language,finish,edition,condition,quantity,seller_id,buy_now_enabled,buy_now_price_pence,keep_buy_now_after_bid,reservation_expires_at,promoted_until,trading_card_game,trading_card_game_other,rarity,rarity_other,grading_status,is_autographed,autograph_details,is_serial_numbered,serial_number,category,subcategory,attributes";



/** Sort helper: featured (promoted) listings first, preserving inner order. */
export function sortFeaturedFirst<T extends { promoted_until?: string | null }>(rows: T[]): T[] {
  const now = Date.now();
  const isPromoted = (r: T) =>
    !!r.promoted_until && new Date(r.promoted_until).getTime() > now;
  const promoted = rows.filter(isPromoted);
  const rest = rows.filter((r) => !isPromoted(r));
  return [...promoted, ...rest];
}

/** Fetch active auctions with an active paid promotion window. */
export async function fetchFeaturedPromoted(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .gt("promoted_until", nowIso)
    .order("promoted_until", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

/** Buy It Now availability filter (client-side + server-side match). */
export function isBuyNowAvailable(a: {
  buy_now_enabled?: boolean | null;
  buy_now_price_pence?: number | null;
  keep_buy_now_after_bid?: boolean | null;
  bid_count?: number | null;
  reservation_expires_at?: string | null;
}): boolean {
  if (!a.buy_now_enabled || !a.buy_now_price_pence) return false;
  if ((a.bid_count ?? 0) > 0 && !a.keep_buy_now_after_bid) return false;
  if (a.reservation_expires_at && new Date(a.reservation_expires_at) > new Date()) return false;
  return true;
}

/**
 * Hero featured picks: admin-featured first, then ending soon, then highest bid.
 * Deduped, capped at `limit`.
 */
export async function fetchHeroFeatured(limit = 3): Promise<AuctionCardData[]> {
  await ensureClosed();
  const nowIso = new Date().toISOString();
  const picked = new Map<string, AuctionCardData>();
  const take = (rows: AuctionCardData[] | null | undefined) => {
    for (const r of rows ?? []) {
      if (picked.size >= limit) break;
      if (!picked.has(r.id)) picked.set(r.id, r);
    }
  };

  const { data: featured } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .eq("featured", true)
    .gt("end_time", nowIso)
    .order("end_time", { ascending: true })
    .limit(limit);
  take(featured as AuctionCardData[] | null);

  if (picked.size < limit) {
    const { data: ending } = await supabase
      .from("auctions")
      .select(CARD_COLS)
      .eq("status", "active")
      .gt("end_time", nowIso)
      .order("end_time", { ascending: true })
      .limit(limit * 2);
    take(ending as AuctionCardData[] | null);
  }

  if (picked.size < limit) {
    const { data: top } = await supabase
      .from("auctions")
      .select(CARD_COLS)
      .eq("status", "active")
      .gt("end_time", nowIso)
      .order("current_bid_pence", { ascending: false, nullsFirst: false })
      .order("starting_price_pence", { ascending: false })
      .limit(limit * 2);
    take(top as AuctionCardData[] | null);
  }

  return [...picked.values()].slice(0, limit);
}

/** Fetch active auctions with Buy It Now currently available. */
export async function fetchBuyNowActive(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .eq("buy_now_enabled", true)
    .gt("end_time", new Date().toISOString())
    .order("created_at", { ascending: false })
    .limit(limit * 2);
  if (error) throw error;
  return ((data ?? []) as AuctionCardData[]).filter(isBuyNowAvailable).slice(0, limit);
}


async function ensureClosed() {
  // Best-effort: close expired auctions on any list fetch.
  // Only authenticated users have EXECUTE on the RPC (pg_cron sweeps it every
  // minute for everyone else); calling as anon returned 401 on every list fetch.
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.rpc("close_expired_auctions");
  } catch {
    /* ignore */
  }
}

export async function fetchEndingSoon(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .gt("end_time", new Date().toISOString())
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchNewlyListed(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchFeatured(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .eq("featured", true)
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  const rows = (data ?? []) as AuctionCardData[];
  if (rows.length > 0) return rows;
  // Fallback: highest-bid active auctions
  const { data: fallback } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .order("current_bid_pence", { ascending: false, nullsFirst: false })
    .limit(limit);
  return (fallback ?? []) as AuctionCardData[];
}

export async function fetchAllActive(): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .order("end_time", { ascending: true });
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchEndingTonight(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .gt("end_time", new Date().toISOString())
    .lte("end_time", end.toISOString())
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchRecentlyApproved(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchHighestValue(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .order("current_bid_pence", { ascending: false, nullsFirst: false })
    .order("starting_price_pence", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchPsa10(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .eq("grading_company", "PSA")
    .eq("grade", "10")
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchBgsBlackLabel(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .eq("grading_company", "BGS")
    .eq("grade", "10")
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchByKeyword(keywords: string[], limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const filter = keywords
    .flatMap((k) => [`title.ilike.%${k}%`, `card_set.ilike.%${k}%`])
    .join(",");
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .or(filter)
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchHighValue(minPence = 100000, limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .or(`current_bid_pence.gte.${minPence},starting_price_pence.gte.${minPence}`)
    .order("current_bid_pence", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchLowStartingBid(maxPence = 2000, limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .lte("starting_price_pence", maxPence)
    .order("starting_price_pence", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchMostWatched(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data: watches } = await supabase.from("watchlist").select("auction_id");
  const counts = new Map<string, number>();
  for (const w of watches ?? []) {
    counts.set(w.auction_id, (counts.get(w.auction_id) ?? 0) + 1);
  }
  const topIds = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([id]) => id);
  if (topIds.length === 0) {
    // Fallback: most bids
    const { data } = await supabase
      .from("auctions")
      .select(CARD_COLS)
      .eq("status", "active")
      .order("bid_count", { ascending: false, nullsFirst: false })
      .limit(limit);
    return (data ?? []) as AuctionCardData[];
  }
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .in("id", topIds)
    .eq("status", "active");
  if (error) throw error;
  const rows = (data ?? []) as AuctionCardData[];
  return rows.sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0));
}


export async function fetchJapanese(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .or("title.ilike.%japanese%,card_set.ilike.%japanese%,title.ilike.%jp %,card_set.ilike.%jp %")
    .order("end_time", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

const MODERN_SETS = [
  "sword", "shield", "scarlet", "violet", "sun", "moon", "xy",
  "evolving", "obsidian", "paldea", "crown zenith", "151", "surging",
];
const VINTAGE_SETS = [
  "base", "jungle", "fossil", "team rocket", "gym", "neo", "expedition",
  "aquapolis", "skyridge",
];

export async function fetchModernHits(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const filter = MODERN_SETS.map((s) => `card_set.ilike.%${s}%`).join(",");
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .or(filter)
    .order("current_bid_pence", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}

export async function fetchVintageGrails(limit = 8): Promise<AuctionCardData[]> {
  await ensureClosed();
  const filter =
    VINTAGE_SETS.map((s) => `card_set.ilike.%${s}%`).join(",") +
    ",title.ilike.%1st edition%,title.ilike.%shadowless%";
  const { data, error } = await supabase
    .from("auctions")
    .select(CARD_COLS)
    .eq("status", "active")
    .or(filter)
    .order("current_bid_pence", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AuctionCardData[];
}
